import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type ModalViewState = "collapsed" | "mini" | "expanded";

interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
  data?: Blob;
  s3_key?: string;
  s3_url?: string;
  file_name?: string;
  content_type?: string;
  size_in_bytes?: number;
  isLoading?: boolean;
  uploadError?: string;
}

interface EmailTemplate {
  _id: string;
  name: string;
  subject: string;
  content: string;
  created_by: string;
  last_updated_by: string;
  created_at: string;
  updated_at: string;
  attachments?: {
    s3_key: string;
    file_name: string;
    content_type: string;
    size_in_bytes: number;
  }[];
}

interface EmailDraft {
  content: string;
  subject: string;
  recipients: string[];
  ccList: string[];
  bccList: string[];
  attachments: Attachment[];
}

interface ComposeEmailModalState {
  isOpen: boolean;
  viewState: ModalViewState;
  leadId: string;
  leadEmail: string;
  isSubjectRequired: boolean;
  isSubjectEditable: boolean;
  content: string;
  subject: string;
  recipients: string[];
  ccList: string[];
  bccList: string[];
  attachments: Attachment[];
  selectedTemplateId: string | null;
  selectedTemplate: EmailTemplate | null;
  selectedTab: string;
  title: string;
  drafts: Record<string, EmailDraft>;
  isFromQuickActions: boolean;
  templatesRowsPerPage: number;
  templatesCurrentPage: number;
  nylas_thread_id: string;
  isReply: boolean;
  source: string;
  sourceMetadata: Record<string, unknown>;
  templateVariableMapping: Record<string, string>;
}

const initialState: ComposeEmailModalState = {
  isOpen: false,
  viewState: "mini",
  leadId: "",
  leadEmail: "",
  isSubjectRequired: true,
  isSubjectEditable: true,
  isReply: false,
  content: "",
  subject: "",
  recipients: [],
  ccList: [],
  bccList: [],
  attachments: [],
  selectedTemplateId: null,
  selectedTemplate: null,
  selectedTab: "create-email",
  title: "New Email",
  drafts: {},
  isFromQuickActions: false,
  templatesRowsPerPage: 10,
  templatesCurrentPage: 1,
  nylas_thread_id: "",
  source: "",
  sourceMetadata: {},
  templateVariableMapping: {},
};

interface OpenComposeEmailModalPayload {
  leadId?: string;
  leadEmail?: string;
  isSubjectRequired?: boolean;
  isSubjectEditable?: boolean;
  isReply?: boolean;
  title?: string;
  viewState?: ModalViewState;
  isFromQuickActions?: boolean;
  nylas_thread_id?: string;
  subject?: string;
  source?: string;
  sourceMetadata?: Record<string, unknown>;
  templateVariableMapping?: Record<string, string>;
}

export const composeEmailModalSlice = createSlice({
  name: "composeEmailModal",
  initialState,
  reducers: {
    openComposeEmailModal: (
      state,
      action: PayloadAction<OpenComposeEmailModalPayload>
    ) => {
      const {
        leadId = "",
        leadEmail = "",
        isSubjectRequired = true,
        isSubjectEditable = true,
        isReply = false,
        title = "New Email",
        viewState = "expanded",
        isFromQuickActions = false,
        nylas_thread_id = "",
        subject = "",
        source = "",
        sourceMetadata = {},
        templateVariableMapping = {},
      } = action.payload;

      const existingDraft = leadId ? state.drafts[leadId] : null;

      state.isOpen = true;
      state.viewState = viewState;
      state.leadId = leadId;
      state.leadEmail = leadEmail;
      state.isSubjectRequired = isSubjectRequired;
      state.isSubjectEditable = isSubjectEditable;
      state.isReply = isReply;
      state.title = title;
      state.source = source;
      state.sourceMetadata = sourceMetadata;
      state.isFromQuickActions = isFromQuickActions;
      state.nylas_thread_id = nylas_thread_id;
      state.templateVariableMapping = templateVariableMapping;

      if (existingDraft) {
        state.content = existingDraft.content;
        state.subject = existingDraft.subject || subject;
        state.recipients = existingDraft.recipients;
        state.ccList = existingDraft.ccList;
        state.bccList = existingDraft.bccList;
        state.attachments = existingDraft.attachments;
      } else {
        state.content = "";
        state.subject = subject ?? "";
        state.recipients = leadEmail ? [leadEmail] : [];
        state.ccList = [];
        state.bccList = [];
        state.attachments = [];
      }
    },
    closeComposeEmailModal: (state) => {
      const drafts = { ...state.drafts };
      if (state.leadId) {
        delete drafts[state.leadId];
      }
      return { ...initialState, drafts };
    },
    dismissComposeEmailModal: (state) => {
      const drafts = { ...state.drafts };
      const hasContent =
        state.content.trim() !== "" ||
        state.subject.trim() !== "" ||
        state.ccList.length > 0 ||
        state.bccList.length > 0 ||
        state.attachments.length > 0;

      if (state.leadId && hasContent) {
        drafts[state.leadId] = {
          content: state.content,
          subject: state.subject,
          recipients: state.recipients,
          ccList: state.ccList,
          bccList: state.bccList,
          attachments: state.attachments,
        };
      }
      return { ...initialState, drafts };
    },
    showDraftIfExists: (
      state,
      action: PayloadAction<{ leadId: string; leadEmail: string }>
    ) => {
      const { leadId, leadEmail } = action.payload;
      const existingDraft = leadId ? state.drafts[leadId] : null;

      if (existingDraft) {
        state.isOpen = true;
        state.viewState = "collapsed";
        state.leadId = leadId;
        state.leadEmail = leadEmail;
        state.isFromQuickActions = false;
        state.title = existingDraft.subject
          ? `Email: ${existingDraft.subject}`
          : "New Email";
        state.content = existingDraft.content;
        state.subject = existingDraft.subject;
        state.recipients = existingDraft.recipients;
        state.ccList = existingDraft.ccList;
        state.bccList = existingDraft.bccList;
        state.attachments = existingDraft.attachments;
      }
    },
    saveDraftForLead: (state) => {
      if (state.leadId) {
        state.drafts[state.leadId] = {
          content: state.content,
          subject: state.subject,
          recipients: state.recipients,
          ccList: state.ccList,
          bccList: state.bccList,
          attachments: state.attachments,
        };
      }
    },
    clearDraftForLead: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload];
    },
    setViewState: (state, action: PayloadAction<ModalViewState>) => {
      state.viewState = action.payload;
    },
    setContent: (state, action: PayloadAction<string>) => {
      state.content = action.payload;
    },
    setSubject: (state, action: PayloadAction<string>) => {
      state.subject = action.payload;
    },
    setRecipients: (state, action: PayloadAction<string[]>) => {
      state.recipients = action.payload;
    },
    setCcList: (state, action: PayloadAction<string[]>) => {
      state.ccList = action.payload;
    },
    setBccList: (state, action: PayloadAction<string[]>) => {
      state.bccList = action.payload;
    },
    setAttachments: (
      state,
      action: PayloadAction<
        Attachment[] | ((prev: Attachment[]) => Attachment[])
      >
    ) => {
      if (typeof action.payload === "function") {
        state.attachments = action.payload(state.attachments);
      } else {
        state.attachments = action.payload;
      }
    },
    setSelectedTemplateId: (state, action: PayloadAction<string | null>) => {
      state.selectedTemplateId = action.payload;
    },
    setSelectedTemplate: (
      state,
      action: PayloadAction<EmailTemplate | null>
    ) => {
      state.selectedTemplate = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    updateEmailData: (
      state,
      action: PayloadAction<
        Partial<
          Pick<
            ComposeEmailModalState,
            | "content"
            | "subject"
            | "recipients"
            | "ccList"
            | "bccList"
            | "attachments"
          >
        >
      >
    ) => {
      const { content, subject, recipients, ccList, bccList, attachments } =
        action.payload;
      if (content !== undefined) state.content = content;
      if (subject !== undefined) state.subject = subject;
      if (recipients !== undefined) state.recipients = recipients;
      if (ccList !== undefined) state.ccList = ccList;
      if (bccList !== undefined) state.bccList = bccList;
      if (attachments !== undefined) state.attachments = attachments;
    },
    cycleViewState: (state) => {
      if (state.viewState === "collapsed") {
        state.viewState = "mini";
      } else if (state.viewState === "mini") {
        state.viewState = "expanded";
      } else {
        state.viewState = "mini";
      }
    },
    collapseModal: (state) => {
      if (state.viewState === "expanded") {
        state.viewState = "mini";
      } else if (state.viewState === "mini") {
        state.viewState = "collapsed";
      }
    },
    expandModal: (state) => {
      if (state.viewState === "collapsed") {
        state.viewState = "mini";
      } else if (state.viewState === "mini") {
        state.viewState = "expanded";
      }
    },
    setTemplatesRowsPerPage: (state, action: PayloadAction<number>) => {
      state.templatesRowsPerPage = action.payload;
    },
    setTemplatesCurrentPage: (state, action: PayloadAction<number>) => {
      state.templatesCurrentPage = action.payload;
    },
  },
});

export const {
  openComposeEmailModal,
  closeComposeEmailModal,
  dismissComposeEmailModal,
  showDraftIfExists,
  saveDraftForLead,
  clearDraftForLead,
  setViewState,
  setContent,
  setSubject,
  setRecipients,
  setCcList,
  setBccList,
  setAttachments,
  setSelectedTemplateId,
  setSelectedTemplate,
  setSelectedTab,
  setTitle,
  updateEmailData,
  cycleViewState,
  collapseModal,
  expandModal,
  setTemplatesRowsPerPage,
  setTemplatesCurrentPage,
} = composeEmailModalSlice.actions;

export default composeEmailModalSlice.reducer;
